<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

const openTrips = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const processingStatusId = ref(null);

const form = ref({
    id: null,
    title: '',
    slug: '',
    description: '',
    destination: '',
    image: null,
    requirements: '',
    meta_title: '',
    meta_description: '',
    sort_order: 0,
    status: true,
    featured: false
});

const originalData = ref(null);

// Modal Logic
const errorModalContent = ref([]);
const modalType = ref('error');

const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    const modal = new bootstrap.Modal(document.getElementById('errorModal'));
    modal.show();
};

// CKEditor Instance
let descriptionEditor = null;

const initEditors = () => {
    if (typeof CKEDITOR === 'undefined') {
        return;
    }

    if (CKEDITOR.instances['description']) {
        try { CKEDITOR.instances['description'].destroy(); } catch (e) {}
    }

    descriptionEditor = CKEDITOR.replace('description', {
        height: 200,
        toolbar: [
            { name: 'styles', items: ['Format'] },
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        format_tags: 'p;h1;h2;h3;h4;h5;h6'
    });

    descriptionEditor.on('change', () => {
        form.value.description = descriptionEditor.getData();
    });
};

onBeforeUnmount(() => {
    if (typeof CKEDITOR !== 'undefined') {
        if (CKEDITOR.instances['description']) try { CKEDITOR.instances['description'].destroy(); } catch (e) {}
    }
});

const loadOpenTrips = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/open-trip/get');
        if (response.data.status === 'success') {
            openTrips.value = response.data.data;
        }
    } catch (error) {
        // Silent error
    } finally {
        loading.value = false;
    }
};

const slugify = (text) => {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

watch(() => form.value.title, (newVal) => {
    if (!isEditing.value && newVal) {
        form.value.slug = slugify(newVal);
    }
});

const handleFileChange = (e) => {
    form.value.image = e.target.files[0];
};

const submitForm = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));

    // Update form data from editor
    if (descriptionEditor) form.value.description = descriptionEditor.getData();

    // Validation
    const errors = [];
    if (!form.value.title || form.value.title.trim() === '') {
        errors.push('Judul Trip wajib diisi');
    }
    if (!form.value.destination || form.value.destination.trim() === '') {
        errors.push('Destinasi wajib diisi');
    }
    if (!form.value.description || form.value.description.trim() === '') {
        errors.push('Deskripsi wajib diisi');
    }

    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    const formData = new FormData();
    formData.append('title', form.value.title);
    formData.append('slug', form.value.slug);
    formData.append('description', form.value.description);
    formData.append('destination', form.value.destination);
    formData.append('requirements', form.value.requirements);
    formData.append('meta_title', form.value.meta_title);
    formData.append('meta_description', form.value.meta_description);
    formData.append('sort_order', form.value.sort_order);

    if (form.value.image) {
        formData.append('image', form.value.image);
    }

    let url = '/admin/open-trip';
    if (isEditing.value) {
        formData.append('_method', 'PUT');
        url = `/admin/open-trip/${form.value.id}`;
    }

    try {
        const response = await axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.status === 'success') {
            resetForm();
            loadOpenTrips();
        } else if (response.data.status === 'info') {
            showModal('warning', [response.data.message]);
        }
    } catch (error) {
        if (error.response && error.response.data) {
            if (error.response.status === 422) {
                const serverErrors = [];
                if (error.response.data.errors) {
                    Object.values(error.response.data.errors).forEach(err => {
                        serverErrors.push(...err);
                    });
                } else if (error.response.data.message) {
                    serverErrors.push(error.response.data.message);
                }
                showModal('error', serverErrors.length > 0 ? serverErrors : ['Validasi gagal.']);
            } else {
                showModal('error', [error.response.data.message || 'Terjadi kesalahan pada server.']);
            }
        } else {
            showModal('error', ['Terjadi kesalahan yang tidak diketahui.']);
        }
    } finally {
        saving.value = false;
    }
};

const edit = (item) => {
    form.value = {
        id: item.id,
        title: item.title,
        slug: item.slug || '',
        description: item.description || '',
        destination: item.destination || '',
        image: null,
        requirements: item.requirements || '',
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        sort_order: item.sort_order || 0,
        status: !!item.status,
        featured: !!item.featured
    };
    isEditing.value = true;

    if (descriptionEditor) {
        descriptionEditor.setData(item.description || '');
    }

    originalData.value = {
        title: item.title,
        slug: item.slug || '',
        description: item.description || '',
        destination: item.destination || '',
        requirements: item.requirements || '',
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        sort_order: item.sort_order || 0
    };

    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const resetForm = () => {
    isEditing.value = false;
    originalData.value = null;
    form.value = {
        id: null,
        title: '',
        slug: '',
        description: '',
        destination: '',
        image: null,
        requirements: '',
        meta_title: '',
        meta_description: '',
        sort_order: 0,
        status: true,
        featured: false
    };
    if (descriptionEditor) descriptionEditor.setData('');
    const fileInput = document.getElementById('image');
    if (fileInput) fileInput.value = '';
};

const toggleStatus = async (trip) => {
    processingStatusId.value = trip.id;
    try {
        const response = await axios.post(`/admin/open-trip/${trip.id}/toggle-status`);
        if (response.data.status === 'success') {
            const index = openTrips.value.findIndex(t => t.id === trip.id);
            if (index !== -1) {
                openTrips.value[index].status = !openTrips.value[index].status;
            }
        }
    } catch (error) {
        showModal('error', ['Gagal mengubah status.']);
    } finally {
        processingStatusId.value = null;
    }
};

const toggleFeatured = async (trip) => {
    try {
        const response = await axios.post(`/admin/open-trip/${trip.id}/toggle-featured`);
        if (response.data.status === 'success') {
            const index = openTrips.value.findIndex(t => t.id === trip.id);
            if (index !== -1) {
                openTrips.value[index].featured = !openTrips.value[index].featured;
            }
        }
    } catch (error) {
        showModal('error', ['Gagal mengubah status featured.']);
    }
};

// Delete Logic
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.title;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value) return;
    try {
        await axios.delete(`/admin/open-trip/${deleteId.value}`);
        const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
        loadOpenTrips();
    } catch (error) {
        showModal('error', ['Gagal menghapus data.']);
        const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
    }
};

onMounted(() => {
    loadOpenTrips();
    nextTick(() => {
        initEditors();
    });
});
</script>

<template>
    <Head title="Kelola Open Trip" />

    <div class="container-full">
        <!-- Breadcrumb Section -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Open Trip</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Open Trip</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Left Column: Form -->
                <div class="col-lg-5 col-12 mb-4">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-plus me-2" v-if="!isEditing"></i>
                                <i class="ti-pencil me-2" v-else></i>
                                {{ isEditing ? 'Edit Open Trip' : 'Tambah Open Trip' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="box-body">
                                <div class="form-group mb-3">
                                    <label class="form-label" for="title">Judul Trip <span class="text-danger">*</span></label>
                                    <input type="text" id="title" v-model="form.title" class="form-control" placeholder="Judul trip" autocomplete="off">
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="slug">Slug</label>
                                    <input type="text" id="slug" v-model="form.slug" class="form-control" placeholder="slug-trip" autocomplete="off">
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="destination">Destinasi <span class="text-danger">*</span></label>
                                    <input type="text" id="destination" v-model="form.destination" class="form-control" placeholder="Contoh: Kawah Ijen" autocomplete="off">
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="description">Deskripsi <span class="text-danger">*</span></label>
                                    <textarea id="description" name="description" class="form-control"></textarea>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="requirements">Persyaratan / Info Tambahan</label>
                                    <textarea id="requirements" v-model="form.requirements" class="form-control" rows="3" placeholder="Persyaratan atau info tambahan"></textarea>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="image">Gambar Trip</label>
                                    <input type="file" id="image" @change="handleFileChange" class="form-control" accept="image/*">
                                    <small v-if="isEditing" class="text-muted">Kosongkan jika tidak ingin mengganti gambar.</small>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="sort_order">Urutan</label>
                                    <input type="number" id="sort_order" v-model="form.sort_order" class="form-control" min="0">
                                </div>

                                <!-- SEO Meta Data Card -->
                                <div class="card bg-light-light mb-0 mt-3">
                                    <div class="card-body py-3">
                                        <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                        <div class="form-group mb-3">
                                            <label class="form-label" for="meta_title">Meta Title</label>
                                            <input type="text" id="meta_title" v-model="form.meta_title" class="form-control" placeholder="Judul pencarian Google">
                                        </div>
                                        <div class="form-group mb-0">
                                            <label class="form-label" for="meta_description">Meta Description</label>
                                            <textarea id="meta_description" v-model="form.meta_description" class="form-control" rows="3" placeholder="Deskripsi di hasil pencarian"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-warning me-2" @click="resetForm" :disabled="saving">
                                    <i class="ti-trash me-2"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Right Column: Table -->
                <div class="col-lg-7 col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Open Trip</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Gambar</th>
                                            <th>Trip</th>
                                            <th class="text-nowrap" style="width: 1%;">Status</th>
                                            <th class="text-center text-nowrap" style="width: 1%;">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading"><td colspan="5" class="text-center p-5"><div class="spinner-border text-primary"></div></td></tr>
                                        <tr v-if="!loading && openTrips.length === 0">
                                            <td colspan="5" class="text-center py-4">Belum ada open trip yang ditambahkan.</td>
                                        </tr>
                                        <tr v-else v-for="(item, index) in openTrips" :key="item.id" class="align-middle">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <img v-if="item.image" :src="'/storage/open-trips/' + item.image" class="rounded" style="width: 60px; height: 40px; object-fit: cover;">
                                                <span v-else class="text-muted small">No Image</span>
                                            </td>
                                            <td>
                                                <div class="fw-bold">{{ item.title }}</div>
                                                <small class="text-muted"><i class="fa fa-map-marker me-1"></i>{{ item.destination }}</small>
                                            </td>
                                            <td class="text-nowrap">
                                                <div class="d-flex flex-column gap-1">
                                                    <button type="button" class="btn btn-sm rounded-1 px-2" :class="item.status ? 'btn-success' : 'btn-danger'" @click="toggleStatus(item)" :disabled="processingStatusId === item.id" title="Toggle Status">
                                                        <span v-if="processingStatusId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                        <i v-else class="fa me-1" :class="item.status ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                        {{ item.status ? 'Aktif' : 'Nonaktif' }}
                                                    </button>
                                                    <button type="button" class="btn btn-sm rounded-1 px-2" :class="item.featured ? 'btn-warning' : 'btn-secondary'" @click="toggleFeatured(item)" title="Toggle Featured">
                                                        <i class="fa me-1" :class="item.featured ? 'fa-star' : 'fa-star-o'"></i>
                                                        {{ item.featured ? 'Featured' : 'Biasa' }}
                                                    </button>
                                                </div>
                                            </td>
                                            <td class="text-center text-nowrap">
                                                <div class="btn-group">
                                                    <button class="btn btn-primary btn-sm" @click="edit(item)" title="Edit"><i class="fa fa-edit"></i></button>
                                                    <button class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Delete Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="ti-alert me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus open trip <strong>{{ deleteName }}</strong>?</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">
                            <i class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
